import React, { useState } from "react";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import dayjs from "dayjs";
import { processExam } from "../../../components/Api/ExamGradingEndpoints";
import { Button } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useAtomValue } from "jotai";
import {
	examProcessingStateAtom,
	allStudentsMatchedAtom,
	examInDatabaseAtom,
	gradeWithoutMatchingAtom,
	classListFileAtom,
	currentOmrRequestAtom,
} from "../../../components/Atoms/atoms";
import {
	buildKeyMapByVersion,
	gradeResponseString,
	isWeightedExam,
	normalizeSelectedQuestions,
} from "../utils/scoring";
import { getSectionGroupsFromStudents, parseRootClassIds } from "../utils/classSections";
import {
	getAnalysisKeyModificationSummary,
	renderKeyModificationSummary,
} from "../utils/keyModificationSummary";
import {
	compareVersionCodesForDisplay,
	normalizeVersionCode,
	normalizeVersionList,
	resolveDisplayVersionCode,
	resolveStudentVersion,
} from "../utils/versionResolution";

const DISPLAY_GAP_MIN_BLANK_RUN = 10;
const LOW_DISCRIMINATING_POWER_THRESHOLD = 0.1;
const HIGH_LOW_GROUP_CORRECT_PROPORTION_THRESHOLD = 0.95;

function formatFixed(value, digits = 2) {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) return Number(0).toFixed(digits);
	const roundedText = numericValue.toFixed(digits);
	return Object.is(Number(roundedText), -0) ? Number(0).toFixed(digits) : roundedText;
}

function parseLeadingNumericValue(value) {
	const numericValue = Number.parseFloat(String(value ?? ""));
	return Number.isFinite(numericValue) ? numericValue : null;
}

function isNegativeDiscriminatingPower(value) {
	const numericValue = parseLeadingNumericValue(value);
	return numericValue != null && numericValue < 0;
}

function isLowDiscriminatingPower(value) {
	const numericValue = parseLeadingNumericValue(value);
	return (
		numericValue != null &&
		numericValue >= 0 &&
		numericValue <= LOW_DISCRIMINATING_POWER_THRESHOLD
	);
}

function isHighLowGroupCorrectProportion(value) {
	const numericValue = parseLeadingNumericValue(value);
	return (
		numericValue != null &&
		numericValue >= HIGH_LOW_GROUP_CORRECT_PROPORTION_THRESHOLD
	);
}

function detectDisplayGapFromUsedKey(entries, lqnum = 1) {
	if (!Array.isArray(entries) || entries.length === 0) {
		return { isNonContiguous: false, excludedRanges: [], displaySegments: [] };
	}

	const startQ = Number(lqnum) || 1;
	const isBlankToken = (value) => String(value ?? "").trim() === "";
	const excludedRanges = [];
	let blankRunStart = null;

	const closeBlankRun = (endIndex) => {
		if (blankRunStart == null) return;
		const runLength = endIndex - blankRunStart + 1;
		if (runLength >= DISPLAY_GAP_MIN_BLANK_RUN) {
			excludedRanges.push({
				startQ: startQ + blankRunStart,
				endQ: startQ + endIndex,
			});
		}
		blankRunStart = null;
	};

	entries.forEach((value, index) => {
		if (isBlankToken(value)) {
			if (blankRunStart == null) blankRunStart = index;
			return;
		}
		closeBlankRun(index - 1);
	});
	closeBlankRun(entries.length - 1);

	const excludedSet = new Set();
	excludedRanges.forEach(({ startQ, endQ }) => {
		for (let q = startQ; q <= endQ; q += 1) excludedSet.add(q);
	});

	const displaySegments = [];
	let currentSegment = [];
	entries.forEach((value, index) => {
		const qNo = startQ + index;
		if (excludedSet.has(qNo)) {
			if (currentSegment.length > 0) {
				displaySegments.push(currentSegment);
				currentSegment = [];
			}
			return;
		}
		currentSegment.push({ qNo, value });
	});
	if (currentSegment.length > 0) {
		displaySegments.push(currentSegment);
	}

	return {
		isNonContiguous: excludedRanges.length > 0,
		excludedRanges,
		displaySegments,
	};
}

function buildExcludedGapSet(gapInfo) {
	if (!gapInfo?.isNonContiguous || !Array.isArray(gapInfo?.excludedRanges)) return null;
	const excluded = new Set();
	gapInfo.excludedRanges.forEach(({ startQ, endQ }) => {
		for (let q = Number(startQ); q <= Number(endQ); q += 1) excluded.add(q);
	});
	return excluded;
}

function formatExcludedRangeMessage(gapInfo) {
	const ranges = gapInfo?.excludedRanges || [];
	if (ranges.length === 0) return "";
	if (ranges.length === 1) {
		const { startQ, endQ } = ranges[0];
		return startQ === endQ
			? `Item ${startQ} was excluded from scoring.`
			: `Items ${startQ}-${endQ} were excluded from scoring.`;
	}
	return `${ranges
		.map(({ startQ, endQ }) => (startQ === endQ ? `item ${startQ}` : `items ${startQ}-${endQ}`))
		.join(", ")} were excluded from scoring.`;
}

function getResponseCharForQuestion(resp, questionNo, lqnum, hqnum) {
	const chars = Array.from(String(resp || ""));
	if (chars.length === 0) return "";

	const q = Number(questionNo);
	const lq = Number(lqnum);
	const hq = Number(hqnum);

	let idx;
	if (Number.isFinite(hq) && hq > 0 && chars.length >= hq) {
		idx = q - 1;
	} else if (Number.isFinite(lq)) {
		idx = q - lq;
	} else {
		idx = q - 1;
	}

	return Number.isInteger(idx) && idx >= 0 && idx < chars.length ? chars[idx] || "" : "";
}

function getEffectiveAnswerArray(item) {
	if (!item || String(item?.omit || "N").toUpperCase() === "Y") return [];
	const modified = Array.isArray(item?.modified_answer) ? item.modified_answer : [];
	const original = Array.isArray(item?.original_answer)
		? item.original_answer
		: item?.original_answer
			? [item.original_answer]
			: [];
	const source = modified.length > 0 ? modified : original;
	return source
		.map((answer) =>
			String(answer || "")
				.trim()
				.toUpperCase(),
		)
		.filter(Boolean);
}

function normalizeCriterionWeight(rawWeight) {
	const parsed = Number.parseFloat(rawWeight);
	if (!Number.isFinite(parsed) || parsed <= 0) return 1;
	return Number(parsed.toFixed(3));
}

function getCriterionScoringSettings(outputSettings = {}) {
	return {
		...outputSettings,
		scalingEnabled: false,
		scalingTarget: "",
		bonusMarks: "",
	};
}

function getKeyForAnalysisVersion(answerKeys, versionCode) {
	const keys = Array.isArray(answerKeys) ? answerKeys : [];
	const target = normalizeVersionCode(
		versionCode,
		normalizeVersionCode(keys[0]?.version, "single"),
	);
	return (
		keys.find((key) => normalizeVersionCode(key?.version, "single") === target) || keys[0] || null
	);
}

function buildCombinedCriterionKey(answerKeys = [], header = {}) {
	const itemsByQuestion = new Map();
	const lqnum = Number(header?.lqnum) || 0;
	const hqnum = Number(header?.hqnum) || 0;

	for (const key of answerKeys || []) {
		for (const item of key?.items || []) {
			const itemNo = Number(item?.item_no);
			if (!Number.isFinite(itemNo)) continue;
			if (itemNo < lqnum || itemNo > hqnum) continue;
			if (itemsByQuestion.has(itemNo)) continue;
			if (getEffectiveAnswerArray(item).length === 0) continue;
			itemsByQuestion.set(itemNo, {
				...item,
				item_no: itemNo,
			});
		}
	}

	const mergedItems = [];
	for (let q = lqnum; q <= hqnum; q += 1) {
		const existing = itemsByQuestion.get(q);
		if (existing) {
			mergedItems.push(existing);
			continue;
		}
		mergedItems.push({
			item_no: q,
			omit: "Y",
			weight: 1,
			original_answer: [""],
			modified_answer: [],
		});
	}

	return {
		version: "C",
		items: mergedItems,
	};
}

function normalizeCriterionScore(rawValue) {
	const score = Number(rawValue);
	if (!Number.isFinite(score)) return 0;
	return Number(score.toFixed(3));
}

function summarizeCriterionScores(scores = []) {
	const cleaned = (scores || []).map((score) => normalizeCriterionScore(score));
	const n = cleaned.length;
	if (n === 0) {
		return {
			count: 0,
			meanRaw: 0,
			varianceRaw: 0,
			sdRaw: 0,
		};
	}

	const meanRaw = cleaned.reduce((sum, value) => sum + value, 0) / n;
	const varianceRaw = cleaned.reduce((sum, value) => sum + Math.pow(value - meanRaw, 2), 0) / n;
	return {
		count: n,
		meanRaw,
		varianceRaw,
		sdRaw: Math.sqrt(varianceRaw),
	};
}

function buildCriterionHistogramScores(
	scores = [],
	{ targetMinScore = 0, targetMaxScore = 0 } = {},
) {
	const cleaned = (scores || []).map((score) => normalizeCriterionScore(score));
	const rawMin = cleaned.length > 0 ? Math.min(...cleaned) : 0;
	const rawMax = cleaned.length > 0 ? Math.max(...cleaned) : 0;
	const normalizedTargetMin = Number.isFinite(Number(targetMinScore)) ? Number(targetMinScore) : 0;
	const normalizedTargetMax = Number.isFinite(Number(targetMaxScore))
		? Number(targetMaxScore)
		: normalizedTargetMin;
	const scoreMin = Math.min(normalizedTargetMin, normalizedTargetMax);
	const scoreMax = Math.max(normalizedTargetMin, normalizedTargetMax);
	const scaleScore = (score) => {
		if (Math.abs(scoreMax - scoreMin) < 1e-9) return scoreMin;
		if (Math.abs(rawMax - rawMin) < 1e-9) return scoreMin;
		const scaledValue =
			scoreMin + ((score - rawMin) / (rawMax - rawMin)) * (scoreMax - scoreMin);
		return Math.max(scoreMin, Math.min(scoreMax, Math.floor(scaledValue + 1e-9)));
	};

	const scaledScores = cleaned.map(scaleScore);
	const total = cleaned.length;
	const freqByScaledScore = new Map();
	for (const scaledScore of scaledScores) {
		freqByScaledScore.set(scaledScore, (freqByScaledScore.get(scaledScore) || 0) + 1);
	}

	let cumulative = 0;
	const scoreDistTable = [];
	for (let scaledScore = scoreMin; scaledScore <= scoreMax; scaledScore += 1) {
		const freq = freqByScaledScore.get(scaledScore) || 0;
			cumulative += freq;
			const leftTailProb = total > 0 ? (cumulative - freq / 2) / total : 0.5;
			const clampedProb = Math.min(1 - 1e-12, Math.max(1e-12, leftTailProb));
			const rawScore =
				Math.abs(scoreMax - scoreMin) < 1e-9
					? rawMin
					: rawMin + ((scaledScore - scoreMin) / (scoreMax - scoreMin)) * (rawMax - rawMin);
			scoreDistTable.push({
				raw_score: rawScore,
				score: scaledScore,
				freq,
				z: inverseNormalCdf(clampedProb),
				pct: clampedProb * 100,
				cum_pct: total > 0 ? (cumulative / total) * 100 : 0,
			});
	}

	return {
		rawMin,
		rawMax,
		scaledScores,
		scoreDistTable,
		histogramTable: scoreDistTable.map(({ score, freq, z, pct, cum_pct }) => ({
			score,
			freq,
			z,
			pct,
			cum_pct,
		})),
	};
}

function groupStudentsByCriterion(studentEntries = [], hiLoPct = 27) {
	const scores = (studentEntries || [])
		.map((entry) => normalizeCriterionScore(entry?.criterionScore))
		.sort((left, right) => left - right);
	const totalCount = scores.length;

	if (totalCount < 3) {
		return {
			groups: {
				low: [],
				mid: studentEntries,
				high: [],
				total: studentEntries,
			},
			lowCut: null,
			highCut: null,
		};
	}

	const freq = new Map();
	for (const score of scores) {
		freq.set(score, (freq.get(score) || 0) + 1);
	}

	let cumulative = 0;
	const boundaries = Array.from(freq.entries())
		.sort((left, right) => left[0] - right[0])
		.map(([score, count]) => {
			cumulative += count;
			return {
				score,
				cumPct: (cumulative * 100) / totalCount,
			};
		});

	const lowTarget = hiLoPct;
	const highTarget = 100 - hiLoPct;
	const lowBoundary = boundaries.reduce((best, boundary) =>
		Math.abs(boundary.cumPct - lowTarget) < Math.abs(best.cumPct - lowTarget) ? boundary : best,
	);
	const highBoundary = boundaries.reduce((best, boundary) =>
		Math.abs(boundary.cumPct - highTarget) < Math.abs(best.cumPct - highTarget) ? boundary : best,
	);

	const lowCut = lowBoundary?.score ?? null;
	const highCut = highBoundary?.score ?? null;
	const groups = { low: [], mid: [], high: [], total: studentEntries };

	for (const entry of studentEntries || []) {
		const score = normalizeCriterionScore(entry?.criterionScore);
		if (lowCut != null && score <= lowCut) {
			groups.low.push(entry);
		} else if (highCut != null && score > highCut) {
			groups.high.push(entry);
		} else {
			groups.mid.push(entry);
		}
	}

	return { groups, lowCut, highCut };
}

function getCriterionItemWeight(keyObj, questionNo) {
	const item = (keyObj?.items || []).find(
		(candidate) => Number(candidate?.item_no) === Number(questionNo),
	);
	return normalizeCriterionWeight(item?.weight);
}

function getCriterionOptionToken(entry, questionNo, header) {
	const raw = getResponseCharForQuestion(entry?.resp, questionNo, header?.lqnum, header?.hqnum);
	const normalized = String(raw || "")
		.trim()
		.toUpperCase();
	return normalized || " ";
}

function getCriterionBreakdownForGroups(groups, items, header) {
	const breakdown = {};
	const allGroupNames = ["total", "high", "mid", "low"];

	for (const groupName of allGroupNames) {
		const students = groups?.[groupName] || [];
		const countsByItem = {};
		const proportionsByItem = {};

		for (const item of items || []) {
			const questionNo = Number(header?.lqnum) + Number(item?.index || 0) - 1;
			const counts = { nf: 0 };
			for (const student of students) {
				const token = getCriterionOptionToken(student, questionNo, header);
				counts[token] = (counts[token] || 0) + 1;
			}
			const optionKeys = item?.options || [];
			const proportions = {};
			for (const option of optionKeys) {
				proportions[option] = students.length > 0 ? (counts[option] || 0) / students.length : 0;
			}

			countsByItem[item.index] = counts;
			proportionsByItem[item.index] = proportions;
		}

		breakdown[groupName] = {
			counts: countsByItem,
			proportions: proportionsByItem,
			n: students.length,
		};
	}

	const optionStats = {};
	const highCount = breakdown?.high?.n || 0;
	const lowCount = breakdown?.low?.n || 0;

	for (const item of items || []) {
		const questionNo = Number(header?.lqnum) + Number(item?.index || 0) - 1;
		optionStats[item.index] = {};

		for (const option of item?.options || []) {
			const scoresForChoice = (groups?.total || [])
				.filter((student) => getCriterionOptionToken(student, questionNo, header) === option)
				.map((student) => normalizeCriterionScore(student?.criterionScore));
			const meanScore =
				scoresForChoice.length > 0
					? scoresForChoice.reduce((sum, score) => sum + score, 0) / scoresForChoice.length
					: 0;
			const pHigh = breakdown?.high?.proportions?.[item.index]?.[option] || 0;
			const pLow = breakdown?.low?.proportions?.[item.index]?.[option] || 0;
			const dp = pHigh - pLow;
			const seDp =
				highCount > 0 && lowCount > 0
					? Math.sqrt((pHigh * (1 - pHigh)) / highCount + (pLow * (1 - pLow)) / lowCount)
					: 0;

			optionStats[item.index][option] = {
				mean_score: meanScore,
				dp,
				se_dp: seDp,
			};
		}
	}

	breakdown.option_stats = optionStats;
	return breakdown;
}

function inverseNormalCdf(probability) {
	const p = Math.min(1 - 1e-12, Math.max(1e-12, Number(probability) || 0));
	const a = [
		-39.6968302866538, 220.946098424521, -275.928510446969, 138.357751867269, -30.6647980661472,
		2.50662827745924,
	];
	const b = [
		-54.4760987982241, 161.585836858041, -155.698979859887, 66.8013118877197, -13.2806815528857,
	];
	const c = [
		-0.00778489400243029, -0.322396458041136, -2.40075827716184, -2.54973253934373,
		4.37466414146497, 2.93816398269878,
	];
	const d = [0.00778469570904146, 0.32246712907004, 2.445134137143, 3.75440866190742];
	const plow = 0.02425;
	const phigh = 1 - plow;

	if (p < plow) {
		const q = Math.sqrt(-2 * Math.log(p));
		return (
			(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
			((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
		);
	}

	if (p > phigh) {
		const q = Math.sqrt(-2 * Math.log(1 - p));
		return -(
			(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
			((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
		);
	}

	const q = p - 0.5;
	const r = q * q;
	return (
		((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) /
		(((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
	);
}

function inverseErf(value) {
	const clamped = Math.min(0.999999999999, Math.max(-0.999999999999, Number(value) || 0));
	return inverseNormalCdf((clamped + 1) / 2) / Math.sqrt(2);
}

function buildCriterionDisplayAnalysis({
	baseAnalysis,
	studentEntries,
	header,
	displayVersion,
	numberOfKeys,
	isCompositeWeightedExam,
}) {
	const normalizedDisplayVersion = normalizeVersionCode(displayVersion, "single");
	const scopedEntries =
		numberOfKeys > 1 && !isCompositeWeightedExam
			? (studentEntries || []).filter(
					(entry) =>
						normalizeVersionCode(entry?.resolvedVersion, "single") === normalizedDisplayVersion,
				)
			: studentEntries || [];

	const criterionScores = scopedEntries.map((entry) =>
		normalizeCriterionScore(entry?.criterionScore),
	);
	const { groups, lowCut, highCut } = groupStudentsByCriterion(scopedEntries, 27);
	const histogramMaxScore =
		Number(baseAnalysis?.k_effective) ||
		(baseAnalysis?.items || []).filter((item) => !item?.removed).length;
	const normalScoreValues = (baseAnalysis?.score_dist_table || [])
		.map((row) => Number(row?.score))
		.filter((value) => Number.isFinite(value));
	const targetMinScore = normalScoreValues.length > 0 ? Math.min(...normalScoreValues) : 0;
	const targetMaxScore =
		normalScoreValues.length > 0 ? Math.max(...normalScoreValues) : histogramMaxScore;
	const histogram = buildCriterionHistogramScores(criterionScores, {
		targetMinScore,
		targetMaxScore,
	});
	const summary = summarizeCriterionScores(criterionScores);
	const groupBreakdown = getCriterionBreakdownForGroups(groups, baseAnalysis?.items || [], header);
	const sdTotal = summary.sdRaw;
	const totalCount = scopedEntries.length;

	const items = (baseAnalysis?.items || []).map((item) => {
		if (item?.removed) {
			return {
				...item,
				dif: 0,
				rpb: 0,
				crpb: 0,
				rbis: 0,
				crbis: 0,
				iri: 0,
				con: [0, 0],
			};
		}

		const questionNo = Number(header?.lqnum) + Number(item?.index || 0) - 1;
		let correctCount = 0;
		const correctScores = [];
		const incorrectScores = [];
		const correctedTotals = [];
		const correctedScoresCorrect = [];
		const correctedScoresIncorrect = [];

		for (const entry of scopedEntries) {
			const answerSet = entry?.answerSetByQ?.[questionNo] || new Set();
			const token = getCriterionOptionToken(entry, questionNo, header);
			const isCorrect = answerSet.size > 0 && token !== " " && answerSet.has(token);
			const criterionScore = normalizeCriterionScore(entry?.criterionScore);
			const contribution = isCorrect ? getCriterionItemWeight(entry?.keyObj, questionNo) : 0;
			const correctedCriterion = normalizeCriterionScore(criterionScore - contribution);

			if (isCorrect) {
				correctCount += 1;
				correctScores.push(criterionScore);
				correctedScoresCorrect.push(correctedCriterion);
			} else {
				incorrectScores.push(criterionScore);
				correctedScoresIncorrect.push(correctedCriterion);
			}
			correctedTotals.push(correctedCriterion);
		}

		const pValue = totalCount > 0 ? correctCount / totalCount : 0;
		const qValue = 1 - pValue;

		let rpb = 0;
		if (sdTotal > 0 && pValue > 0 && pValue < 1) {
			const meanCorrect =
				correctScores.length > 0
					? correctScores.reduce((sum, score) => sum + score, 0) / correctScores.length
					: 0;
			const meanIncorrect =
				incorrectScores.length > 0
					? incorrectScores.reduce((sum, score) => sum + score, 0) / incorrectScores.length
					: 0;
			rpb = ((meanCorrect - meanIncorrect) * Math.sqrt(pValue * qValue)) / sdTotal;
		}

		const correctedSummary = summarizeCriterionScores(correctedTotals);
		const sdCorrected = correctedSummary.sdRaw;
		let crpb = 0;
		if (sdCorrected > 0 && pValue > 0 && pValue < 1) {
			const meanCorrectedCorrect =
				correctedScoresCorrect.length > 0
					? correctedScoresCorrect.reduce((sum, score) => sum + score, 0) /
						correctedScoresCorrect.length
					: 0;
			const meanCorrectedIncorrect =
				correctedScoresIncorrect.length > 0
					? correctedScoresIncorrect.reduce((sum, score) => sum + score, 0) /
						correctedScoresIncorrect.length
					: 0;
			crpb =
				((meanCorrectedCorrect - meanCorrectedIncorrect) * Math.sqrt(pValue * qValue)) /
				sdCorrected;
		}

		let lo = 0;
		let hi = 0;
		if (totalCount > 3 && Math.abs(crpb) < 1) {
			const z = 0.5 * Math.log((1 + crpb) / (1 - crpb));
			const standardError = 1 / Math.sqrt(totalCount - 3);
			const margin = 1.96 * standardError;
			lo = Math.tanh(z - margin);
			hi = Math.tanh(z + margin);
		}

		let rbis = 0;
		let crbis = 0;
		if (pValue > 0 && pValue < 1) {
			const zThreshold = Math.sqrt(2) * inverseErf(2 * pValue - 1);
			const normalDensity =
				(1 / Math.sqrt(2 * Math.PI)) * Math.exp((-1 * zThreshold * zThreshold) / 2);
			if (normalDensity > 0) {
				rbis = (rpb * Math.sqrt(pValue * qValue)) / normalDensity;
				crbis = (crpb * Math.sqrt(pValue * qValue)) / normalDensity;
			}
		}

		return {
			...item,
			dif: pValue,
			rpb,
			crpb,
			rbis,
			crbis,
			iri: rpb * Math.sqrt(pValue * (1 - pValue)),
			con: [lo, hi],
		};
	});

	return {
		...baseAnalysis,
		score_dist_table: histogram.histogramTable,
		criterion_score_dist_table: histogram.scoreDistTable,
		criterion_histogram_table: histogram.histogramTable,
		group_breakdown: groupBreakdown,
		items,
		low_cut: lowCut,
		high_cut: highCut,
		criterion_raw_min: histogram.rawMin,
		criterion_raw_max: histogram.rawMax,
	};
}

function buildCriterionStudentEntries({
	students,
	answerKeys,
	header,
	outputSettings,
	isCompositeWeightedExam,
}) {
	const scoringSettings = getCriterionScoringSettings(outputSettings);
	const knownVersions = normalizeVersionList((answerKeys || []).map((key) => key?.version));
	const defaultVersion = normalizeVersionCode(knownVersions[0], "single");
	const combinedKey = isCompositeWeightedExam
		? buildCombinedCriterionKey(answerKeys, header)
		: null;
	const keyMapByVersion = buildKeyMapByVersion(combinedKey ? [combinedKey] : answerKeys || []);
	const combinedAnswerSetByQ = combinedKey && (keyMapByVersion.C || keyMapByVersion.single || {});

	return (students || []).map((student) => {
		const resp = student?.std_response ?? student?.response ?? student?.std_response_str ?? "";

		if (combinedKey) {
			const { score } = gradeResponseString(
				resp,
				combinedAnswerSetByQ || {},
				combinedKey,
				header,
				scoringSettings,
			);
			return {
				student,
				resp,
				resolvedVersion: "C",
				keyObj: combinedKey,
				answerSetByQ: combinedAnswerSetByQ || {},
				criterionScore: normalizeCriterionScore(score),
			};
		}

		const formCodeInfo = resolveStudentVersion(student, knownVersions, "single");
		const resolvedVersion = normalizeVersionCode(formCodeInfo?.resolvedVersion, defaultVersion);
		const keyObj = getKeyForAnalysisVersion(answerKeys, resolvedVersion);
		const keyVersion = normalizeVersionCode(keyObj?.version, defaultVersion);
		const answerSetByQ = keyMapByVersion[keyVersion] || keyMapByVersion.single || {};
		const { score } = gradeResponseString(resp, answerSetByQ, keyObj, header, scoringSettings);

		return {
			student,
			resp,
			resolvedVersion: keyVersion,
			keyObj,
			answerSetByQ,
			criterionScore: normalizeCriterionScore(score),
		};
	});
}

function appendSuffixToFilename(filename, filenameSuffix = "") {
	if (!filenameSuffix || !filename) return filename;
	const dotIndex = filename.lastIndexOf(".");
	if (dotIndex <= 0) return `${filename}${filenameSuffix}`;
	return `${filename.slice(0, dotIndex)}${filenameSuffix}${filename.slice(dotIndex)}`;
}

function triggerBlobDownload(blob, filename) {
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = filename;
	anchor.click();
	URL.revokeObjectURL(url);
}

function getAnswerKeyVersions(examProcessingState) {
	return normalizeVersionList(
		(examProcessingState?.answer_keys || []).map(
			(key) => key?.version ?? key?.special_code ?? key?.data?.special_code,
		),
	).sort(compareVersionCodesForDisplay);
}

function isReorderedItemAnalysis(examProcessingState, outputSettings) {
	return Boolean(
		examProcessingState?.reorder_analysis?.analysis && outputSettings?.reorderedItemAnalysis,
	);
}

function shouldSplitItemAnalysisByVersion(examProcessingState, outputSettings) {
	return Boolean(
		outputSettings?.oldExamResultFormat &&
		getAnswerKeyVersions(examProcessingState).length > 1 &&
		outputSettings?.printAllVersions !== false &&
		!isReorderedItemAnalysis(examProcessingState, outputSettings),
	);
}

function buildItemAnalysisFilename({ reqnum, sectionClassId = null, versionLabel = "" }) {
	const parts = [String(reqnum || "exam"), "itm"];
	const normalizedVersionLabel = String(versionLabel || "").trim();
	if (normalizedVersionLabel) parts.push(normalizedVersionLabel);
	const normalizedSectionLabel = String(sectionClassId || "").trim();
	if (normalizedSectionLabel) parts.push(normalizedSectionLabel);
	return `${parts.join("_")}.pdf`;
}

function formatScoreBand(group = {}) {
	const scoreMin = Number(group?.score_min);
	const scoreMax = Number(group?.score_max);
	if (!Number.isFinite(scoreMin) || !Number.isFinite(scoreMax)) return "";
	if (scoreMin === scoreMax) return `${scoreMin}`;
	return `${scoreMin}-${scoreMax}`;
}

function buildQuintileBandText(quintiles, { includeCounts = false } = {}) {
	if (!quintiles?.enabled || !Array.isArray(quintiles?.groups) || quintiles.groups.length === 0) {
		return "";
	}

	return quintiles.groups
		.map((group) => {
			const scoreBand = formatScoreBand(group);
			if (!scoreBand) return "";
			const countSuffix = includeCounts ? ` (N=${group.n || 0})` : "";
			return `${group.label}=${scoreBand}${countSuffix}`;
		})
		.filter(Boolean)
		.join(", ");
}

function buildQuintileSummaryText(quintiles) {
	const groupText = buildQuintileBandText(quintiles);
	if (!groupText) return "";
	return `A separate quintile statistics section is included after the per-item statistics. Q1 is the lowest score band and Q5 the highest. Score bands: ${groupText}.`;
}

function formatCriterionDistributionValue(value) {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) return "";
	if (numericValue < 100) {
		return formatFixed(Math.floor(numericValue * 10) / 10, 1);
	}
	return String(Math.floor(numericValue));
}

function drawScoreDistributionTable(doc, { pageWidth, distTable, startY = 100, showRawScores = false }) {
	const cellWidth = showRawScores ? 48 : 50;
	const headers = showRawScores
		? ["RAW SCORE", "SCALED SCORE", "FREQ", "Z-NORML", "%-ILE", "CUM-%"]
		: ["SCORE", "FREQ", "Z-NORML", "%-ILE", "CUM-%"];
	const tableWidth = headers.length * cellWidth;
	const marginLeft = (pageWidth - tableWidth) / 2;

	autoTable(doc, {
		head: [headers],
		body: (distTable || []).map((row) =>
			showRawScores
				? [
					formatCriterionDistributionValue(row.raw_score),
					row.score,
					row.freq,
					formatFixed(row.z, 3),
					formatFixed(row.pct, 2),
					formatFixed(row.cum_pct, 2),
				]
				: [
					row.score,
					row.freq,
					formatFixed(row.z, 3),
					formatFixed(row.pct, 2),
					formatFixed(row.cum_pct, 2),
				],
		),
		startY,
		margin: { left: marginLeft },
		theme: "plain",
		headStyles: {
			cellPadding: { top: 1, right: 1, bottom: 8, left: 1 },
			halign: "center",
		},
		bodyStyles: {
			fontSize: 8,
			cellWidth,
			halign: "center",
		},
		styles: {
			fontSize: 8,
			cellPadding: 1,
		},
	});

	return doc.lastAutoTable.finalY;
}

function drawScoreHistogramPage(
	doc,
	{ pageWidth, distTable, title = "HISTOGRAM", xAxisLabel = "CLASS INTERVAL" },
) {
	doc.addPage();

	const margin = 40;
	const histWidth = pageWidth - (margin + 5) * 2;
	const histHeight = 120;
	const histX = margin + 10;
	const histY = margin + 30;
	const freqs = (distTable || []).map((row) => row.freq || 0);
	const maxFreq = Math.max(1, ...freqs);
	const barGap = 4;

	doc.setLineWidth(0.5);
	doc.line(histX, histY, histX, histY + histHeight);
	doc.line(histX, histY + histHeight, histX + histWidth, histY + histHeight);

	doc.text("FREQUENCY", histX + 20, (histY + histHeight) * 0.8, {
		align: "center",
		angle: 90,
	});

	doc.text(xAxisLabel, histX + histWidth / 2, histY + histHeight + 25, {
		align: "center",
	});

	const step = (distTable || []).length > 50 ? 2 : 1;
	const drawCount = Math.max(1, Math.ceil((distTable || []).length / step));
	const totalGap = barGap * (drawCount - 1);
	const computedBarWidth = (histWidth - totalGap) / drawCount;

	for (let i = 0, drawIndex = 0; i < (distTable || []).length; i += step, drawIndex++) {
		const row = distTable[i];
		const h = ((row.freq || 0) / maxFreq) * (histHeight - 10);
		const x = histX + drawIndex * (computedBarWidth + barGap);
		const y = histY + (histHeight - h);

		doc.setFillColor(39, 93, 56);
		doc.rect(x, y, computedBarWidth, h, "F");

		doc.setFontSize(8);
		doc.text(String(row.score), x + computedBarWidth / 2, histY + histHeight + 8, {
			align: "center",
		});

		if ((row.freq || 0) > 0) {
			doc.text(String(row.freq), x + computedBarWidth / 2, y - 2, {
				align: "center",
			});
		}

		doc.setFontSize(9);
	}

	doc.text(title, pageWidth / 2, histY - 10, {
		align: "center",
	});
}

function renderFrequencyDistributionSection(
	doc,
	{
		pageWidth,
		title,
		distTable,
		histogramTable = distTable,
		introTexts = [],
		summaryTexts = [],
		showRawScores = false,
		histogramTitle = "HISTOGRAM",
		xAxisLabel = "CLASS INTERVAL",
	},
) {
	doc.addPage();
	const titleWidth = doc.getTextWidth(title);
	doc.text(title, pageWidth / 2 - titleWidth / 2, 70);

	let tableStartY = 100;
	for (const text of introTexts.filter(Boolean)) {
		const lines = doc.splitTextToSize(text, pageWidth - 100);
		doc.text(lines, 50, tableStartY);
		tableStartY += lines.length * 10 + 12;
	}

	const lastY = drawScoreDistributionTable(doc, {
		pageWidth,
		distTable,
		startY: tableStartY,
		showRawScores,
	});

	const summaryLineBlocks = summaryTexts
		.filter(Boolean)
		.map((text) => doc.splitTextToSize(text, pageWidth - 100));
	let summaryY = lastY + 20;
	const estimatedBottomY =
		summaryY +
		summaryLineBlocks.reduce((totalHeight, lines) => totalHeight + lines.length * 10 + 12, 0);
	if (estimatedBottomY > doc.internal.pageSize.getHeight() - 40) {
		doc.addPage();
		summaryY = 80;
	}

	for (const lines of summaryLineBlocks) {
		doc.text(lines, 50, summaryY);
		summaryY += lines.length * 10 + 15;
	}

	drawScoreHistogramPage(doc, {
		pageWidth,
		distTable: histogramTable,
		title: histogramTitle,
		xAxisLabel,
	});
}

function getQuintileDifficultyPoints(quintiles, item) {
	const groups = Array.isArray(quintiles?.groups) ? [...quintiles.groups].reverse() : [];
	const correctOptions = new Set(
		(item?.correct_options || [])
			.map((option) => String(option || "").trim())
			.filter(Boolean),
	);

	return groups.map((group) => {
		const proportions = quintiles?.breakdown?.[group.key]?.proportions?.[item.index] || {};
		let diffScore = 0;
		correctOptions.forEach((option) => {
			diffScore += Number(proportions?.[option] || 0);
		});

		return {
			...group,
			diffScore: Math.max(0, Math.min(1, diffScore)),
		};
	});
}

function drawQuintileDifficultyChart(doc, { x, y, width, height, item, itemNo, quintiles }) {
	const cardRadius = 6;
	const borderColor = [205, 211, 217];
	const accentColor = [39, 93, 56];
	const guideColor = [222, 226, 230];
	const axisColor = [110, 117, 124];
	const points = getQuintileDifficultyPoints(quintiles, item);
	const correctOptionLabel = (item?.correct_options || []).join("/") || "-";

	doc.setDrawColor(...borderColor);
	doc.roundedRect(x, y, width, height, cardRadius, cardRadius, "S");

	doc.setFontSize(10);
	doc.setTextColor(0, 0, 0);
	doc.text(`ITEM ${itemNo}`, x + width / 2, y + 16, { align: "center" });
	doc.setFontSize(8);
	doc.text(`Correct: ${correctOptionLabel}`, x + width / 2, y + 28, { align: "center" });

	if (item?.removed || points.length === 0) {
		doc.setFontSize(8);
		doc.text("No quintile graph available for this item.", x + width / 2, y + height / 2, {
			align: "center",
		});
		return;
	}

	const plotLeft = x + 42;
	const plotTop = y + 42;
	const plotWidth = width - 58;
	const plotHeight = height - 66;
	const plotBottom = plotTop + plotHeight;
	const plotRight = plotLeft + plotWidth;
	const verticalSteps = 10;

	doc.setDrawColor(...guideColor);
	doc.setLineWidth(0.5);
	for (let step = 0; step <= verticalSteps; step += 1) {
		const tickRatio = step / verticalSteps;
		const tickX = plotLeft + tickRatio * plotWidth;
		doc.line(tickX, plotTop, tickX, plotBottom);
		doc.setFontSize(7);
		doc.setTextColor(...axisColor);
		doc.text(formatFixed(tickRatio, 1), tickX, plotBottom + 10, { align: "center" });
	}

	const yStep = points.length > 1 ? plotHeight / (points.length - 1) : 0;
	points.forEach((point, index) => {
		const tickY = plotTop + index * yStep;
		doc.line(plotLeft, tickY, plotRight, tickY);
		doc.setFontSize(8);
		doc.setTextColor(0, 0, 0);
		doc.text(point.label, plotLeft - 6, tickY + 3, { align: "right" });
	});

	doc.setDrawColor(...axisColor);
	doc.setLineWidth(0.75);
	doc.line(plotLeft, plotTop, plotLeft, plotBottom);
	doc.line(plotLeft, plotBottom, plotRight, plotBottom);

	const plottedPoints = points.map((point, index) => ({
		...point,
		x: plotLeft + point.diffScore * plotWidth,
		y: plotTop + index * yStep,
	}));

	doc.setDrawColor(...accentColor);
	doc.setLineWidth(1.2);
	for (let index = 0; index < plottedPoints.length - 1; index += 1) {
		const current = plottedPoints[index];
		const next = plottedPoints[index + 1];
		doc.line(current.x, current.y, next.x, next.y);
	}

	plottedPoints.forEach((point) => {
		doc.setFillColor(...accentColor);
		doc.circle(point.x, point.y, 2.5, "F");
		doc.setFontSize(7);
		doc.setTextColor(...accentColor);
		const valueX = point.x > plotLeft + plotWidth - 20 ? point.x - 6 : point.x + 6;
		const align = valueX < point.x ? "right" : "left";
		const valueText = formatFixed(point.diffScore, 2);
		if (point.label === "Q1") {
			doc.setFillColor(255, 255, 255);
			const textWidth = doc.getTextWidth(valueText);
			const labelX = align === "right" ? valueX - textWidth : valueX;
			doc.roundedRect(labelX - 2, point.y - 4, textWidth + 4, 8, 1.5, 1.5, "F");
		}
		doc.text(valueText, valueX, point.y + 2, { align });
	});

	doc.setFontSize(7);
	doc.setTextColor(0, 0, 0);
	doc.text("DIFFICULTY", plotLeft + plotWidth / 2 - 10, y + height - 5, { align: "center" });
	doc.setFontSize(8);
}

function drawQuintileDifficultyCharts(doc, { items, quintiles, header, startY, pageWidth, pageHeight }) {
	const marginX = 40;
	const columnGap = 18;
	const cardWidth = (pageWidth - marginX * 2 - columnGap) / 2;
	const cardHeight = 150;
	const rowGap = 18;
	const bottomMargin = 40;
	const graphItems = (items || []).filter((item) => !item?.removed);

	if (graphItems.length === 0) {
		doc.setFontSize(9);
		doc.text("No non-removed items are available for quintile graphs.", marginX, startY);
		return;
	}

	let currentY = startY;
	let columnIndex = 0;

	graphItems.forEach((item, index) => {
		if (columnIndex === 0 && currentY + cardHeight > pageHeight - bottomMargin) {
			doc.addPage();
			currentY = 60;
		}

		const x = marginX + columnIndex * (cardWidth + columnGap);
		const itemNo = Number(header?.lqnum) + Number(item?.index || 0) - 1;
		drawQuintileDifficultyChart(doc, {
			x,
			y: currentY,
			width: cardWidth,
			height: cardHeight,
			item,
			itemNo,
			quintiles,
		});

		const shouldAdvanceRow = columnIndex === 1 || index === graphItems.length - 1;
		if (shouldAdvanceRow) {
			currentY += cardHeight + rowGap;
			columnIndex = 0;
		} else {
			columnIndex = 1;
		}
	});
}

export default function ItemAnalysisPDF({
	token,
	reqnum,
	downloadingAll,
	outputSettings,
	filenameSuffix = "",
}) {
	const [loadingItemAnalysis, setLoadingItemAnalysis] = useState(false);
	const examProcessingState = useAtomValue(examProcessingStateAtom);
	const allStudentsMatched = useAtomValue(allStudentsMatchedAtom);
	const examInDatabase = useAtomValue(examInDatabaseAtom);
	const gradeWithoutMatching = useAtomValue(gradeWithoutMatchingAtom);
	const classListFile = useAtomValue(classListFileAtom);
	const currentOmrRequest = useAtomValue(currentOmrRequestAtom);
	const rootClassIds = parseRootClassIds(currentOmrRequest?.class_ids);
	const sectionGroups = getSectionGroupsFromStudents({
		students: examProcessingState?.students || [],
		classListRows: classListFile?.classlist || [],
		rootClassIds,
	});
	const shouldSplitBySection = Boolean(
		outputSettings?.downloadPerClassSection && rootClassIds.length > 1 && sectionGroups.length > 0,
	);
	const selectedQuestions = normalizeSelectedQuestions(
		outputSettings?.selectedQuestions,
		examProcessingState?.header?.lqnum,
		examProcessingState?.header?.hqnum,
	);
	const noQuestionsSelected =
		Array.isArray(outputSettings?.selectedQuestions) && selectedQuestions?.size === 0;

	const processExamData = async () => {
		setLoadingItemAnalysis(true);
		try {
			if (shouldSplitBySection) {
				for (const { classId, students } of sectionGroups) {
					await runItemAnalysisPDFDownloads({
						examProcessingState,
						token,
						outputSettings,
						filenameSuffix,
						sectionClassId: classId,
						sectionStudentRspIds: students.map((student) => student.rsp_id),
					});
				}
				return;
			}

			await runItemAnalysisPDFDownloads({
				examProcessingState,
				token,
				outputSettings,
				filenameSuffix,
			});
		} finally {
			setLoadingItemAnalysis(false);
		}
	};

	return (
		<Button
			onClick={processExamData}
			variant={"outlined"}
			color="primary"
			sx={{ borderRadius: "30px" }}
			disabled={
				!examProcessingState ||
				reqnum !== examProcessingState.header.reqnum ||
				!examInDatabase ||
				(!allStudentsMatched && !gradeWithoutMatching) ||
				noQuestionsSelected ||
				loadingItemAnalysis ||
				downloadingAll
			}
		>
			{loadingItemAnalysis || downloadingAll ? (
				<CircularProgress size={24} color="inherit" />
			) : (
				"Download"
			)}
		</Button>
	);
}

export async function buildItemAnalysisPDFBlob({
	examProcessingState,
	token,
	outputSettings,
	sectionClassId = null,
	sectionStudentRspIds = null,
}) {
	const selectedQuestions = normalizeSelectedQuestions(
		outputSettings?.selectedQuestions,
		examProcessingState?.header?.lqnum,
		examProcessingState?.header?.hqnum,
	);
	const selectedQuestionsPayload = selectedQuestions
		? [...selectedQuestions].sort((a, b) => a - b)
		: null;
	const sectionRspSet = Array.isArray(sectionStudentRspIds)
		? new Set(sectionStudentRspIds.map((value) => String(value)))
		: null;
	const scopedStudents = sectionRspSet
		? (examProcessingState?.students || []).filter((student) =>
				sectionRspSet.has(String(student?.rsp_id)),
			)
		: examProcessingState?.students || [];

	const response = await processExam(token, examProcessingState.header.reqnum, false, {
		header: examProcessingState.header,
		answer_keys: examProcessingState.answer_keys,
		student_responses: scopedStudents,
		selected_questions: selectedQuestionsPayload,
	});

	const numberOfKeys = examProcessingState.answer_keys.length;
	const knownVersions = normalizeVersionList(
		(examProcessingState?.answer_keys || []).map(
			(key) => key?.version ?? key?.special_code ?? key?.data?.special_code,
		),
	);
	const getDisplayVersion = (value) =>
		resolveDisplayVersionCode(value, knownVersions, String(value ?? "").trim());

	const isReorderedExam =
		examProcessingState.reorder_analysis?.analysis && outputSettings.reorderedItemAnalysis;

	const originalMultiVersionAnalyses = [...(response.analyses_by_version || [])].sort((a, b) =>
		compareVersionCodesForDisplay(getDisplayVersion(a.version), getDisplayVersion(b.version)),
	);

	const analyses = isReorderedExam
		? [examProcessingState.reorder_analysis]
		: originalMultiVersionAnalyses;

	const header = response.header;
	const isCompositeWeightedExam =
		Number(header?.e || 0) > 0 || isWeightedExam(examProcessingState?.answer_keys || []);
	const selectedQuestionsForRender = normalizeSelectedQuestions(
		outputSettings?.selectedQuestions,
		header?.lqnum,
		header?.hqnum,
	);
	const criterionScoreDisplayEnabled =
		Boolean(outputSettings?.criterionScoreEnabled) && !isReorderedExam;
	const criterionStudentEntries = criterionScoreDisplayEnabled
		? buildCriterionStudentEntries({
				students: scopedStudents,
				answerKeys: examProcessingState?.answer_keys || [],
				header,
				outputSettings,
				isCompositeWeightedExam,
			})
		: [];

	const doc = new jsPDF({ unit: "pt", format: "letter" });
	const date = dayjs().format("MMDDYY");
	const dateFormatted = dayjs().format("MM/DD/YYYY");
	const time = dayjs().format("HH:mm:ss");

	const lqnum = Number(header.lqnum);

	if (outputSettings.printAllVersions === false) {
		// filter analyses to only include the selected version
		const selectedVersion = getDisplayVersion(outputSettings.versionToPrint);
		const filtered = analyses.filter((a) => getDisplayVersion(a.version) === selectedVersion);
		if (filtered.length > 0) {
			analyses.length = 0;
			analyses.push(filtered[0]);
		}
	}

	// ============================================================================
	// TESTING: Summary Statistics on First Page (all versions)
	// ============================================================================

	// ============================================================================
	const pageVersionByPage = new Map();

	analyses.forEach(({ version, analysis }, vi) => {
		if (vi > 0) {
			doc.addPage();
		}
		const displayVersion = getDisplayVersion(version);
		const criterionDisplayAnalysis = criterionScoreDisplayEnabled
			? buildCriterionDisplayAnalysis({
					baseAnalysis: analysis,
					studentEntries: criterionStudentEntries,
					header,
					displayVersion,
					numberOfKeys,
					isCompositeWeightedExam,
				})
			: null;
		const analysisForCriterionPages = criterionDisplayAnalysis || analysis;
		const analysisStartPage = doc.getNumberOfPages();
		const shouldRenderQuintiles = Boolean(
			outputSettings?.includeQuintileStatistics && analysis.quintiles?.enabled,
		);
		const choices = [
			"A",
			"B",
			"C",
			"D",
			"E",
			"F",
			"G",
			"H",
			"I",
			"J",
			"K",
			"L",
			"M",
			"N",
			"O",
			"P",
			"Q",
			"R",
			"S",
			"T",
			"U",
			"V",
			"W",
			"X",
			"Y",
			"Z",
		];
		const number_of_answer_option = header.qlen;
		const letter_choices = choices.slice(0, number_of_answer_option);
		const letter_choices_str = letter_choices.join("");
		// // — Header block —
		// doc.setFontSize(9);
		// doc.text(`${header.reqnum}`, 100, 40);
		// if (numberOfKeys > 1) {
		// 	doc.text(`Version ${version}`, 160, 40);
		// }
		// doc.text(`${header.coursetitle}`, 250, 40);
		// doc.text(`${date}`, 360, 40);
		doc.setFontSize(9);
		doc.text(`Report generated at ${time} on ${dateFormatted}`, 40, 60);
		let headerYOffset = 0;
		if (sectionClassId) {
			doc.setFont(undefined, "bold");
			doc.text(`SECTION: ${sectionClassId}`, 40, 74);
			headerYOffset += 14;
			doc.setFont(undefined, "normal");
		}

		doc.text(`Run parameters:`, 40, 80 + headerYOffset);
		doc.text(`First Item Number = ${Number(header.lqnum)}`, 50, 90 + headerYOffset);
		doc.text(`Last Item Number = ${Number(header.hqnum)}`, 50, 100 + headerYOffset);
		doc.text(`Number of Choices = ${Number(header.qlen)}`, 50, 110 + headerYOffset);
		doc.text(`Hi/Lo Percentage = 27.00%`, 50, 120 + headerYOffset);
		doc.text(`Translation Table = ${letter_choices_str}`, 50, 130 + headerYOffset);

		// Show discarded blank responses if any
		const discardedCount = analysis.discarded_blank_count || 0;
		if (discardedCount > 0) {
			doc.setTextColor(200, 0, 0); // red text for warning
			doc.text(
				`${discardedCount} data record${discardedCount > 1 ? "s were" : " was"} discarded because all responses were blank.`,
				50,
				145 + headerYOffset,
			);
			doc.setTextColor(0, 0, 0); // reset to black
		}

		const modificationSummary = getAnalysisKeyModificationSummary(analysis);

		const keyIntroStartY = renderKeyModificationSummary(doc, modificationSummary, {
			startY: 160 + headerYOffset,
			marginLeft: 50,
			marginRight: 40,
			lineHeight: 12,
			gapAfter: 8,
		});

		const { used_key } = analysis;
		const entries = used_key.used_key_string.split("|");
		const keyLines = [];
		const perLine = 10; // how many entries per line
		const pageWidth = doc.internal.pageSize.getWidth();

		const blockWidth = pageWidth / 2 + 100; // want to span half the page
		const spacing = blockWidth / (perLine + 1); // equal gaps within that block
		const xOffset = (pageWidth - blockWidth) / 2; // center the block

		const alternateArray = [];
		const gapInfo = detectDisplayGapFromUsedKey(entries, lqnum);
		const isNonContiguousExam = gapInfo.isNonContiguous;
		const excludedRangeMessage = formatExcludedRangeMessage(gapInfo);
		const displaySegments = gapInfo.displaySegments || [];

		if (isNonContiguousExam) {
			let nonContiguousIntroY = keyIntroStartY;
			if (excludedRangeMessage) {
				doc.text(excludedRangeMessage, xOffset, nonContiguousIntroY);
				nonContiguousIntroY += 20;
			}

			doc.text(
				`KEY ${numberOfKeys > 1 ? `${displayVersion}` : ``}`,
				doc.internal.pageSize.getWidth() / 2,
				nonContiguousIntroY + 20,
				{
					align: "center",
				},
			);
			const keyRowsStartY = nonContiguousIntroY + 40;
			let currentY = keyRowsStartY;
			displaySegments.forEach((segment, segmentIndex) => {
				if (segment.length === 0) return;
				const firstQ = segment[0].qNo;
				if (segmentIndex > 0 || firstQ !== Number(lqnum)) {
					doc.text(`Keys shown beginning with Item Number ${firstQ}:`, xOffset, currentY);
					currentY += 20;
				}

				for (let i = 0; i < segment.length; i += perLine) {
					const lineEntries = segment.slice(i, i + perLine);
					const y = currentY + Math.floor(i / perLine) * 20;
					lineEntries.forEach((entry, idx) => {
						const val = String(entry?.value ?? "");
						let valueForDoc = val;
						const x = xOffset + spacing * (idx + 1);
						if (val.includes(",")) {
							alternateArray.push({
								answers: val.split(",").map((answer) => answer.trim()),
								itemNumber: entry.qNo,
							});
							valueForDoc = val.split(",").join("/");
						} else if (val.trim() === "") {
							valueForDoc = "*";
						}
						doc.text(valueForDoc, x, y, { align: "center" });
					});
				}

				currentY += Math.ceil(segment.length / perLine) * 20 + 20;
			});
		} else {
			// center "KEY" header

			doc.setFontSize(12);
			doc.text(
				`KEY ${numberOfKeys > 1 ? `${displayVersion}` : ``}`,
				doc.internal.pageSize.getWidth() / 2,
				keyIntroStartY,
				{
					align: "center",
					fontStyle: "bold",
				},
			);

			doc.setFontSize(9);
			for (let i = 0; i < entries.length; i += perLine) {
				keyLines.push(entries.slice(i, i + perLine));
			}
			let startY = keyIntroStartY + 20;
			const cellWidth = 30;
			const tableWidthFull = perLine * cellWidth; // always 10 columns wide for alignment
			const marginLeftFull = (pageWidth - tableWidthFull) / 2;
			keyLines.forEach((lineEntries, rowIndex) => {
				const headers = lineEntries.map((_, idx) => `Q${lqnum + idx + rowIndex * 10}`);

				autoTable(doc, {
					head: [headers],
					body: [lineEntries],
					startY,
					margin: { left: marginLeftFull }, // <- constant margin keeps last row left-aligned
					theme: "grid",
					headStyles: {
						cellPadding: { top: 1, right: 1, bottom: 1, left: 1 },
						halign: "center",
						fillColor: [150, 150, 150],
						textColor: [255, 255, 255],
					},
					bodyStyles: {
						fontSize: 8,
						cellWidth,
						halign: "center",
						textColor: [0, 0, 0],
					},
					styles: { fontSize: 8, cellPadding: 1 },
				});

				startY = doc.lastAutoTable.finalY + 10;
			});
		}

		const quintileSummaryText = shouldRenderQuintiles
			? buildQuintileSummaryText(analysis.quintiles)
			: "";
		renderFrequencyDistributionSection(doc, {
			pageWidth,
			title: "*** FREQUENCY DISTRIBUTION AND HISTOGRAM OF SUM OF CORRECT RESPONSES ***",
			distTable: analysis.score_dist_table || [],
			histogramTable: analysis.score_dist_table || [],
			summaryTexts: [
				criterionScoreDisplayEnabled
					? ""
					: `The cutting points used to divide the class into HIGH, MIDDLE and LOW groups for the presented statistics are: ${analysis.low_cut} and ${analysis.high_cut}`,
				quintileSummaryText,
			],
		});

		const margin = 40;
		doc.addPage();

		const leftX = margin;
		let y = 60;

		// **** TEST STATISTICS ****
		doc.text("****TEST STATISTICS****", pageWidth / 3, y, {
			align: "center",
		});
		const stat_positions = pageWidth / 3 + 20;
		const stat_positions_right_side = stat_positions + 50;

		y += 30;
		// NUMBER OF EXAMINEES
		doc.text(`NUMBER OF EXAMINEES`, stat_positions, y, {
			align: "right",
		});
		doc.text(`${analysis.student_count}`, stat_positions_right_side, y, {
			align: "right",
		});

		// TOTAL SCORE:
		y += 20;
		doc.text("TOTAL SCORE:", stat_positions - 43, y, {
			align: "right",
		});

		// compute S.E. of Measurement from SD and KR-20
		const sem = formatFixed(analysis.sd_total * Math.sqrt(1 - analysis.kr20), 4);

		// the stats, indented
		y += 12;
		doc.text(`MEAN`, stat_positions, y, {
			align: "right",
		});
		doc.text(`${formatFixed(analysis.mean_total, 2)}`, stat_positions_right_side, y, {
			align: "right",
		});
		y += 12;
		doc.text(`VARIANCE`, stat_positions, y, {
			align: "right",
		});
		doc.text(`${formatFixed(analysis.var_total, 2)}`, stat_positions_right_side, y, {
			align: "right",
		});
		y += 12;
		doc.text(`STANDARD DEVIATION`, stat_positions, y, {
			align: "right",
		});
		doc.text(`${formatFixed(analysis.sd_total, 2)}`, stat_positions_right_side, y, {
			align: "right",
		});
		y += 12;
		doc.text(`KR-20 RELIABILITY`, stat_positions, y, {
			align: "right",
		});
		doc.text(`${formatFixed(analysis.kr20, 4)}`, stat_positions_right_side, y, {
			align: "right",
		});
		y += 12;
		doc.text(`S.E. OF MEASUREMENT`, stat_positions, y, {
			align: "right",
		});
		doc.text(`${sem}`, stat_positions_right_side, y, {
			align: "right",
		});

		// reference blurb
		y += 30;

		doc.text("An overview of item analysis statistics appears in", leftX + 20, y);
		y += 12;
		doc.text("Osterlind, Steven J.   Constructing Test Items. 2nd ed.", leftX + 40, y);
		y += 12;
		doc.text("Boston: Kluwer Academic Publishers, 1998.", leftX + 40, y);

		// GLOSSARY
		y += 30;

		doc.text("G L O S S A R Y", pageWidth / 2, y, { align: "center" });

		const glossary = [
			["DIF:", "DIFFICULTY - the proportion of examinees answering the item correctly"],
			[
				"RPB:",
				criterionScoreDisplayEnabled
					? "Point-biserial correlation between item and criterion score"
					: "Point-biserial correlation between item and total test score",
			],
			[
				"CRPB:",
				criterionScoreDisplayEnabled
					? "Corrected RPB - RPB between item and criterion score not including the present item"
					: "Corrected RPB - RPB between item and total score not including the present item",
			],
			["CON:", "Confidence limits on CRPB"],
			[
				"RBIS:",
				criterionScoreDisplayEnabled
					? "Biserial correlation between item and criterion score"
					: "Biserial correlation between item and total test score",
			],
			["CRBIS:", "Corrected RBIS"],
			["IRI:", "Item reliability index - RPB times square root of DIF times one minus DIF"],
			["N:", "Number of examinees in group"],
			["INV:", "Number of examinees not providing a valid response to this item"],
			["NF:", "Number of examinees not finishing the test from this item onwards"],
			["OMIT:", "Number of examinees omitting this item"],
			["*:", "Indicates the correct answer (keyed response)"],
			[
				"HIGH:",
				criterionScoreDisplayEnabled
					? "Approximately 27.0% of the total group scoring highest on the criterion score"
					: "Approximately 27.0% of the total group scoring highest on the total test",
			],
			
			[
				"LOW:",
				criterionScoreDisplayEnabled
					? "Same proportion as 'HIGH' having lowest criterion scores"
					: "Same proportion as 'HIGH' having lowest scores",
			],
			[
				"MID:",
				criterionScoreDisplayEnabled
					? "The remaining proportion of the total group scoring between the LOW and HIGH groups on the criterion score"
					: "The remaining proportion of the total group scoring between the LOW and HIGH groups on the total test",
			],

			[
				criterionScoreDisplayEnabled ? "CRITERION SCORE MEANS:" : "TEST SCORE MEANS:",
				criterionScoreDisplayEnabled
					? "Means on the criterion score for the individuals giving the indicated response to this item"
					: "Means on the total test for the individuals giving the indicated response to this item",
			],
			[
				"DISCRIMINATING POWER:",
				"The difference between the proportions of the HIGH and LOW groups giving this response",
			],
			["STANDARD ERROR OF D.P.:", "Standard error of discriminating power"],
		];
		if (shouldRenderQuintiles) {
			glossary.splice(14, 0, [
				"Q1-Q5:",
				"Approximate fifths of the total group from lowest to highest total score",
			]);
		}

		y += 20;
		const scoreMeansTerm = criterionScoreDisplayEnabled
			? "CRITERION SCORE MEANS:"
			: "TEST SCORE MEANS:";
		const sectionIndentedTerms = new Set([
			scoreMeansTerm,
			"DISCRIMINATING POWER:",
			"STANDARD ERROR OF D.P.:",
		]);
		glossary.forEach(([term, desc]) => {
			if (term === scoreMeansTerm) {
				y += 6;
			}
			doc.text(term, leftX, y);
			const amount_to_adjust = sectionIndentedTerms.has(term) ? 130 : 40;
			doc.text(desc, leftX + amount_to_adjust, y);
			y += 12;
		});

		y += 6;
		doc.text(
			"Following the numerical report of the item analysis, plots are provided which illustrate the relationship",
			margin,
			y,
		);
		y += 10;
		doc.text(
			criterionScoreDisplayEnabled
				? "between achievement or ability as measured by the criterion score and success on a given item."
				: "between achievement or ability as measured by the total test and success on a given item.",
			margin,
			y,
		);
		y += 12;
		doc.text("These plots are only provided if there is a sufficient number of cases.", margin, y);

		if (criterionScoreDisplayEnabled && criterionDisplayAnalysis) {
			renderFrequencyDistributionSection(doc, {
				pageWidth,
				title: "*** FREQUENCY DISTRIBUTION AND HISTOGRAM OF CRITERION SCORES ***",
				distTable: criterionDisplayAnalysis.criterion_score_dist_table || [],
				histogramTable:
					criterionDisplayAnalysis.criterion_histogram_table ||
					criterionDisplayAnalysis.score_dist_table ||
					[],
				introTexts: [
					"*** References to TEST SCORE from here on mean the CRITERION rather than TOTAL number of correct items ***",
					`The CRITERION has been rescaled for the frequency distribution and histogram to have the same range as the total number correct. UNSCALED MINIMUM = ${formatCriterionDistributionValue(
						criterionDisplayAnalysis.criterion_raw_min || 0,
					)}, MAXIMUM = ${formatCriterionDistributionValue(
						criterionDisplayAnalysis.criterion_raw_max || 0,
					)}.`,
				],
				summaryTexts: [
					`The cutting points used to divide the class into HIGH, MIDDLE and LOW groups for the presented statistics are: ${formatCriterionDistributionValue(
						criterionDisplayAnalysis.low_cut,
					)} and ${formatCriterionDistributionValue(criterionDisplayAnalysis.high_cut)}`,
				],
				showRawScores: true,
				xAxisLabel: "SCALED SCORE",
			});
		}

		doc.addPage();

		let itemsY = 60;

		// — Per-item stats — hide contiguity-gap items determined from used key
		const excludedGapQs = buildExcludedGapSet(gapInfo);
		const itemsToRender = (analysisForCriterionPages.items || []).filter((item) => {
			const itemNo = Number(header.lqnum) + Number(item.index) - 1;
			if (selectedQuestionsForRender && !selectedQuestionsForRender.has(itemNo)) return false;
			if (!excludedGapQs) return true;
			return !excludedGapQs.has(itemNo);
		});

		// — Per-item stats —
		itemsToRender.forEach((item) => {
			const correctOptions = new Set(
				(item.correct_options || []).map((opt) =>
					String(opt || "")
						.trim()
						.toUpperCase(),
				),
			);
			const optionHeaders = item.options.map((opt) =>
				correctOptions.has(
					String(opt || "")
						.trim()
						.toUpperCase(),
				)
					? `${opt}*`
					: opt,
			);
			const head = ["Group", "N", "INV", "NF", "OMIT", ...optionHeaders];
			const rowKinds = [];
			const buildGroupRow = ({ label, key, breakdownRoot }) => {
				const groupStats = breakdownRoot?.[key] || {};
				const cnts = groupStats?.counts?.[item.index] || {};
				const numberOfStudents = groupStats?.n || 0;
				const props = groupStats?.proportions?.[item.index] || {};
				const numberOfInvalid = cnts[" "] || cnts["*"] || 0; // invalid responses
				const numberOfOmitted = cnts[" "] || 0; // omitted responses
				rowKinds.push({ type: "group", key });
				return [
					label,
					numberOfStudents || 0, // not finishing the test from this item onwards
					numberOfInvalid || 0, // invalid responses
					cnts.nf || 0, // not finishing the test from this item onwards TODO: IMPLEMENT THIS
					numberOfOmitted || 0, // omitted responses
					...item.options.map((opt) => {
						const c = cnts[opt] || 0;
						const p = props[opt] != null ? formatFixed(props[opt], 2) : "0.00";
						return `${p} (${c})`;
					}),
				];
			};
			const groupRows = [
				{ label: "TOTAL", key: "total", breakdownRoot: analysisForCriterionPages.group_breakdown },
				{ label: "HIGH", key: "high", breakdownRoot: analysisForCriterionPages.group_breakdown },
				{ label: "MID", key: "mid", breakdownRoot: analysisForCriterionPages.group_breakdown },
				{ label: "LOW", key: "low", breakdownRoot: analysisForCriterionPages.group_breakdown },
			].map(buildGroupRow);

			const correctHeaderIndexes = item.options
				.map((opt, index) =>
					correctOptions.has(
						String(opt || "")
							.trim()
							.toUpperCase(),
					)
						? index + 5
						: -1,
				)
				.filter((i) => i >= 0);
			const optionStats = analysisForCriterionPages.group_breakdown.option_stats[item.index];

			const GREEN = [39, 93, 56];
			const RIGHT_GAP = 2; // <-- space (in pts) you want between the green col and the next col

			function isCorrectCol(data) {
				return correctHeaderIndexes.includes(data.column.index);
			}

			function shouldForceWhite(data) {
				const raw = String(data.cell?.raw ?? "");
				const rowKind = rowKinds[data.row.index];
				return (
					(rowKind?.type === "disc_power" &&
						(isNegativeDiscriminatingPower(raw) || isLowDiscriminatingPower(raw))) ||
					(rowKind?.type === "group" &&
						rowKind?.key === "low" &&
						isHighLowGroupCorrectProportion(raw))
				);
			}

			const testMeansRow = [
				{
					content: criterionScoreDisplayEnabled ? "CRITERION SCORE MEANS" : "TEST SCORE MEANS",
					colSpan: 5,
					styles: { halign: "left", fontStyle: "bold" },
				},
				...item.options.map((opt) => formatFixed(optionStats[opt].mean_score, 2)),
			];
			rowKinds.push({ type: "test_score_means" });
			const discPowerRow = [
				{
					content: "DISCRIMINATING POWER",
					colSpan: 5,
					styles: { halign: "left", fontStyle: "bold" },
				},
				...item.options.map((opt) => formatFixed(optionStats[opt].dp, 2)),
			];
			rowKinds.push({ type: "disc_power" });
			const seDpRow = [
				{
					content: "STANDARD ERROR OF D.P.",
					colSpan: 5,
					styles: { halign: "left", fontStyle: "bold" },
				},
				...item.options.map((opt) => formatFixed(optionStats[opt].se_dp, 2)),
			];
			rowKinds.push({ type: "se_dp" });

			const body = [...groupRows, testMeansRow, discPowerRow, seDpRow];

			// --- estimate the height needed for the header+table ---
			const lineHeight = doc.getLineHeight() * 1.2; // approx height for your ITEM: line
			const headerHeight = lineHeight - 2; // a little padding
			const rowHeight = lineHeight; // each table row
			const tableHeight =
				headerHeight + // the ITEM: line
				rowHeight * (body.length + 1) + // header row + data rows
				8; // bottom padding

			const pageHeight = doc.internal.pageSize.getHeight();
			const bottomMargin = 40;
			const remaining = pageHeight - bottomMargin - itemsY;

			// if it won't all fit, break first
			if (remaining < tableHeight) {
				doc.addPage();
				itemsY = 60; // or wherever your “top of item section” should be
			}

			// now safe to render both together
			// — ITEM heading —

			const lo = formatFixed(item.con[0], 3),
				hi = formatFixed(item.con[1], 3);
			const itemNo = Number(header.lqnum) + item.index - 1;
			//doc.setTextColor(255, 255, 255);
			doc.setFillColor(227, 227, 227);
			doc.rect(35, itemsY - 12, 520, headerHeight + 16, "F");
			doc.text(`ITEM ${itemNo}: `, 40, itemsY);
			if (item.removed) {
				doc.text("(REMOVED)", 100, itemsY);
				itemsY += 10; // some padding after the ITEM line
				itemsY += headerHeight;
			} else {
				doc.text(
					`DIF=${formatFixed(item.dif, 3)}, ` +
						`RPB=${formatFixed(item.rpb, 3)}, CRPB=${formatFixed(item.crpb, 3)} ` +
						`(95% CON ${lo}, ${hi})`,
					85,
					itemsY,
				);
				itemsY += 10; // some padding after the ITEM line
				doc.text(
					`RBIS=${formatFixed(item.rbis, 3)}, ` +
						`CRBIS=${formatFixed(item.crbis, 3)}, IRI=${formatFixed(item.iri, 3)}`,
					85,
					itemsY,
				);
				itemsY += headerHeight;

				// — the table —
			}
			autoTable(doc, {
				head: [head],
				body,
				startY: itemsY,
				theme: "plain",
				styles: { fontSize: 8, cellPadding: 1 },

				didParseCell(data) {
					if (data.section === "head" && data.row.index === 0 && isCorrectCol(data)) {
						// We'll draw the correct-answer marker ourselves; keep the header text readable.
						data.cell.styles.fillColor = null;
						data.cell.styles.textColor = [0, 0, 0];
						data.cell.styles.fontStyle = "bold";
					}

					if (data.section === "body" && isCorrectCol(data)) {
						data.cell.styles.fontStyle = "bold";
						if (shouldForceWhite(data)) {
							const rowKind = rowKinds[data.row.index];
							data.cell.styles.textColor =
								rowKind?.type === "disc_power" &&
								isNegativeDiscriminatingPower(data.cell.raw)
									? [229, 106, 84] // red
									: [246, 141, 46]; // orange
							data.cell.styles.fillColor = null; // no green fill; we won't draw bg for these
						} else {
							data.cell.styles.textColor = [0, 0, 0];
							data.cell.styles.fillColor = null; // prevent full-cell fill
						}
					}
				},

				// Paint a custom background that leaves a right-side gap
				willDrawCell(data) {
					if (!isCorrectCol(data)) return;

					const forceWhite = data.section === "body" && shouldForceWhite(data);
					if (forceWhite) return; // skip drawing green bg for exception rows

					if (data.section === "head" && data.row.index === 0) {
						const { cell } = data;

						// shrink width by RIGHT_GAP to leave white space on the right
						const x = cell.x;
						const y = cell.y;
						const w = Math.max(0, cell.width - RIGHT_GAP);
						const h = cell.height;

						doc.setDrawColor(...GREEN);
						// rounded rect looks a bit nicer; swap for doc.rect(x, y, w, h, 'F') if you want square corners
						// make the green just outline instead of full fill by drawing a white rect on top with some padding

						doc.roundedRect(x - 1, y, w - 35, h, 1.5, 1.5);
					}
				},
			});

			itemsY = doc.lastAutoTable.finalY + 20;
		});

		if (shouldRenderQuintiles) {
			doc.addPage();
			let quintileY = 60;
			doc.setFontSize(11);
			doc.text("****QUINTILE STATISTICS****", pageWidth / 2, quintileY, {
				align: "center",
			});
			quintileY += 22;
			doc.setFontSize(9);
			const quintileIntroLines = doc.splitTextToSize(
				"Quintile statistics are shown for this exam because the response count and score spread were large enough to support five score bands.",
				pageWidth - 80,
			);
			doc.text(quintileIntroLines, 40, quintileY);
			quintileY += quintileIntroLines.length * 12 + 6;

			const quintileBandText = buildQuintileBandText(analysis.quintiles, {
				includeCounts: true,
			});
			if (quintileBandText) {
				const quintileBandLines = doc.splitTextToSize(
					`Score bands: ${quintileBandText}`,
					pageWidth - 80,
				);
				doc.text(quintileBandLines, 40, quintileY);
				quintileY += quintileBandLines.length * 12 + 16;
			}

			drawQuintileDifficultyCharts(doc, {
				items: itemsToRender,
				quintiles: analysis.quintiles,
				header,
				startY: quintileY,
				pageWidth,
				pageHeight: doc.internal.pageSize.getHeight(),
			});
		}

		drawDiscriminationDifficultyChart(doc, itemsToRender, header);
		const analysisEndPage = doc.getNumberOfPages();
		for (let pageNumber = analysisStartPage; pageNumber <= analysisEndPage; pageNumber += 1) {
			pageVersionByPage.set(pageNumber, displayVersion);
		}
	});
	const pageCount = doc.getNumberOfPages();
	for (let i = 1; i <= pageCount; i++) {
		doc.setPage(i);
		// set X to width - length of text to place it at the right edge INSTEAD OF 520

		const text = `Page ${i} of ${pageCount}`;
		const textWidth = doc.getTextWidth(text);
		doc.text(text, doc.internal.pageSize.getWidth() - textWidth - 40, 20);
		doc.text(`${header.reqnum}`, 100, 20);

		const displayVersion = pageVersionByPage.get(i);
		if (numberOfKeys > 1 && displayVersion) {
			doc.text(`Version ${displayVersion}`, 160, 20);
		}
		doc.text(`${header.coursetitle}`, 250, 20);
		doc.text(`${date}`, 360, 20);
	}
	const sectionSuffix = sectionClassId ? `_${sectionClassId}` : "";
	const filename = `${response.header.reqnum}_itm${sectionSuffix}.pdf`;
	const blob = doc.output("blob");
	return { blob, filename };
}

export async function buildItemAnalysisPDFEntries({
	examProcessingState,
	token,
	outputSettings,
	sectionClassId = null,
	sectionStudentRspIds = null,
}) {
	if (shouldSplitItemAnalysisByVersion(examProcessingState, outputSettings)) {
		const knownVersions = getAnswerKeyVersions(examProcessingState);
		const reqnum = examProcessingState?.header?.reqnum || "exam";
		const entries = [];

		for (const versionLabel of knownVersions) {
			const nextOutputSettings = {
				...outputSettings,
				printAllVersions: false,
				versionToPrint: versionLabel,
			};
			const { blob } = await buildItemAnalysisPDFBlob({
				examProcessingState,
				token,
				outputSettings: nextOutputSettings,
				sectionClassId,
				sectionStudentRspIds,
			});
			entries.push({
				blob,
				filename: buildItemAnalysisFilename({
					reqnum,
					sectionClassId,
					versionLabel: resolveDisplayVersionCode(versionLabel, knownVersions, versionLabel),
				}),
			});
		}

		return entries;
	}

	const { blob, filename } = await buildItemAnalysisPDFBlob({
		examProcessingState,
		token,
		outputSettings,
		sectionClassId,
		sectionStudentRspIds,
	});
	return [{ blob, filename }];
}

export async function runItemAnalysisPDF({
	examProcessingState,
	token,
	outputSettings,
	filenameSuffix = "",
	sectionClassId = null,
	sectionStudentRspIds = null,
}) {
	const { blob, filename } = await buildItemAnalysisPDFBlob({
		examProcessingState,
		token,
		outputSettings,
		sectionClassId,
		sectionStudentRspIds,
	});
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = appendSuffixToFilename(filename, filenameSuffix);
	a.click();
	URL.revokeObjectURL(url);
}

export async function runItemAnalysisPDFDownloads({
	examProcessingState,
	token,
	outputSettings,
	filenameSuffix = "",
	sectionClassId = null,
	sectionStudentRspIds = null,
}) {
	const entries = await buildItemAnalysisPDFEntries({
		examProcessingState,
		token,
		outputSettings,
		sectionClassId,
		sectionStudentRspIds,
	});

	for (const entry of entries) {
		triggerBlobDownload(entry.blob, appendSuffixToFilename(entry.filename, filenameSuffix));
	}
}

function getDisplayItemNumber(item, header = {}) {
	const lqnum = Number(header?.lqnum);
	const index = Number(item?.index);
	if (Number.isFinite(lqnum) && Number.isFinite(index)) return lqnum + index - 1;
	return item?.index;
}

function drawDiscriminationDifficultyChart(doc, items, header = {}) {
	doc.addPage();
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();
	const margin = 60;

	doc.setFontSize(10);
	doc.text("DISCRIMINATION X DIFFICULTY CHART", pageWidth / 2, margin - 20, {
		align: "center",
	});

	// Chart dimensions
	const chartX = margin + 30;
	const chartY = margin + 20;
	const chartWidth = pageWidth - chartX - margin;
	const chartHeight = pageHeight - chartY - margin - 100; // reduced to leave room for overlap report

	// Draw axes
	doc.setLineWidth(1.0);
	doc.rect(chartX, chartY, chartWidth, chartHeight); // Bounding box

	// Y-axis label
	doc.setFontSize(9);
	doc.text("B I S E R I A L   C O R R E L A T I O N", chartX + 30, chartY + chartHeight / 2, {
		align: "center",
		angle: 90,
	});

	// X-axis label
	doc.text("D I F F I C U L T Y", chartX + chartWidth / 2, chartY + chartHeight + 35, {
		align: "center",
	});

	// Draw axis ticks and labels
	doc.setFontSize(8);
	doc.setLineWidth(0.5);
	for (let i = 0; i <= 10; i++) {
		const v = i / 10;

		// Y-axis ticks
		const y = chartY + chartHeight - v * chartHeight;
		doc.line(chartX - 5, y, chartX, y);
		doc.text(formatFixed(v, 1), chartX - 10, y + 3, { align: "right" });

		// X-axis ticks
		const x = chartX + v * chartWidth;
		doc.line(x, chartY + chartHeight, x, chartY + chartHeight + 5);
		doc.text(formatFixed(v, 1), x, chartY + chartHeight + 15, { align: "center" });
	}

	// Collect item positions for overlap detection
	const plotted = [];
	const labelFontSize = 7;
	doc.setFontSize(labelFontSize);

	// Approximate label dimensions (width depends on number of digits)
	const charWidth = 4; // approximate width per character at font size 7
	const labelHeight = 6; // approximate height

	items.forEach((item) => {
		if (item.dif === null || item.rbis === null) return;
		if (item.removed) return;

		// Clamp values to be within the 0-1 range for plotting
		const difficulty = Math.max(0, Math.min(1, item.dif));
		const biserial = Math.max(0, Math.min(1, item.rbis));

		const plotX = chartX + difficulty * chartWidth;
		const plotY = chartY + chartHeight - biserial * chartHeight;

		const displayIndex = getDisplayItemNumber(item, header);
		const labelText = String(displayIndex);
		const labelWidth = labelText.length * charWidth;

		plotted.push({
			index: displayIndex,
			x: plotX,
			y: plotY,
			dif: difficulty,
			rbis: biserial,
			width: labelWidth,
			height: labelHeight,
		});
	});

	// Detect overlaps - check if bounding boxes intersect
	const overlaps = [];
	const overlapThreshold = 8; // pixels proximity to consider as overlap

	for (let i = 0; i < plotted.length; i++) {
		for (let j = i + 1; j < plotted.length; j++) {
			const a = plotted[i];
			const b = plotted[j];

			const dx = Math.abs(a.x - b.x);
			const dy = Math.abs(a.y - b.y);

			// Check if labels would overlap (within threshold distance)
			if (
				dx < (a.width + b.width) / 2 + overlapThreshold &&
				dy < (a.height + b.height) / 2 + overlapThreshold
			) {
				overlaps.push({
					first: a.index,
					second: b.index,
					firstDif: a.dif,
					firstRbis: a.rbis,
					secondDif: b.dif,
					secondRbis: b.rbis,
				});
			}
		}
	}

	// Plot items
	doc.setFontSize(7);
	plotted.forEach((item) => {
		doc.text(String(item.index), item.x, item.y, { align: "center" });
	});

	const bottomMargin = 40;
	const reportHeaderHeight = 12;
	const lineHeight = 10;
	const reportStartY = chartY + chartHeight + 50;
	const reportWidth = pageWidth - margin * 2;
	const drawOverflowPageTitle = (continuation = false) => {
		doc.setFontSize(9);
		doc.setTextColor(0, 0, 0);
		doc.text(
			continuation
				? "DISCRIMINATION X DIFFICULTY CHART - REPORT (CONT.)"
				: "DISCRIMINATION X DIFFICULTY CHART - REPORT",
			pageWidth / 2,
			45,
			{ align: "center" },
		);
		return 65;
	};

	let currentY = reportStartY;
	const ensureReportSpace = (height, continuation = false) => {
		if (currentY + height <= pageHeight - bottomMargin) return;
		doc.addPage();
		currentY = drawOverflowPageTitle(continuation);
	};

	const lowRbisItems = (items || [])
		.filter((item) => !item?.removed && Number.isFinite(Number(item?.rbis)) && Number(item.rbis) <= 0)
		.map((item) => getDisplayItemNumber(item, header));
	const lowRbisLines = doc.splitTextToSize(
		`Items with RBIS <= 0.00: ${lowRbisItems.length > 0 ? lowRbisItems.join(", ") : "none"}.`,
		reportWidth,
	);
	ensureReportSpace(lowRbisLines.length * lineHeight, false);
	doc.setFontSize(8);
	doc.setTextColor(0, 0, 0);
	for (const line of lowRbisLines) {
		ensureReportSpace(lineHeight, true);
		doc.text(line, margin, currentY);
		currentY += lineHeight;
	}
	currentY += 4;

	// Draw overlap report below the RBIS note.
	if (overlaps.length > 0) {
		const overlapStrings = overlaps.map((o) => {
			const coord1 = `(${formatFixed(o.firstDif, 2)}, ${formatFixed(o.firstRbis, 2)})`;
			const coord2 = `(${formatFixed(o.secondDif, 2)}, ${formatFixed(o.secondRbis, 2)})`;
			return `${o.first}:${o.second} ${coord1}/${coord2}`;
		});

		// Split into lines if too many overlaps
		const maxPerLine = 4;
		const overlapLines = [];
		for (let i = 0; i < overlapStrings.length; i += maxPerLine) {
			const lineItems = overlapStrings.slice(i, i + maxPerLine);
			overlapLines.push(lineItems.join("  |  "));
		}

		const drawOverlapHeader = (y, continuation = false) => {
			doc.setFontSize(8);
			doc.setTextColor(150, 0, 0);
			doc.text(
				continuation
					? "Items with overlapping points (continued):"
					: "Items with overlapping points (1st obscured by 2nd):",
				margin,
				y,
			);
			doc.setFontSize(7);
			doc.setTextColor(0, 0, 0);
			return y + reportHeaderHeight;
		};

		let lineIndex = 0;

		ensureReportSpace(reportHeaderHeight + overlapLines.length * lineHeight, false);
		currentY = drawOverlapHeader(currentY, false);

		while (lineIndex < overlapLines.length) {
			if (currentY + lineHeight > pageHeight - bottomMargin) {
				doc.addPage();
				currentY = drawOverflowPageTitle(true);
				currentY = drawOverlapHeader(currentY, true);
			}

			doc.text(overlapLines[lineIndex], margin, currentY);
			currentY += lineHeight;
			lineIndex += 1;
		}
	}
}
